import React, { useEffect, useRef, useState } from 'react';
import { partnerApiService } from '../services/api';
import { apiErrorMessage, apiFieldErrors } from '../services/apiErrors';
import { LegalDetail, UpdateLegalRequest } from '../types/api';
import { LEGAL_FORMS, LegalFormValue, identifierLabelFor } from '../config/legalForms';
import { formatOfferAcceptedAt } from '../config/legal';
import { validateRegistrationNumber } from '../utils/registrationNumber';
import { useLanguage } from '../contexts/LanguageContext';
import { FormCard, FormFoot } from './form/FormShell';
import { Field, FieldGrid } from './form/Field';
import { Toast, useToast } from './form/Toast';

interface LegalDetailsEditorProps {
  /** Реквизиты из профиля; `null` — ничего не заполняли, форма пустая. */
  legal: LegalDetail | null;
}

/** id `<form>`: кнопка сохранения живёт в подвале, вне самой формы. */
export const LEGAL_FORM_ID = 'partner-legal-form';

/** Длины из правил `PUT /partner/legal` на бэке: показать ошибку до запроса, а не после. */
const LIMITS = {
  nameMin: 2,
  nameMax: 255,
  addressMin: 5,
  addressMax: 500,
};

type LegalField = keyof UpdateLegalRequest;

type Errors = Partial<Record<LegalField, string>>;

const FIELDS: LegalField[] = ['form', 'name', 'registration_number', 'address'];

interface FormState {
  form: LegalFormValue | '';
  name: string;
  registrationNumber: string;
  address: string;
}

function stateFromLegal(legal: LegalDetail | null): FormState {
  return {
    form: legal?.form ?? '',
    name: legal?.name ?? '',
    registrationNumber: legal?.registration_number ?? '',
    address: legal?.address ?? '',
  };
}

/**
 * Пустое поле — не ошибка: реквизиты необязательные. Проверяем только то,
 * что партнёр ввёл, — теми же правилами, что были в анкете регистрации.
 */
function validate(state: FormState, nameShort: string, addressShort: string): Errors {
  const errors: Errors = {};

  const name = state.name.trim();
  if (name && name.length < LIMITS.nameMin) errors.name = nameShort;

  const number = state.registrationNumber.trim();
  if (number) {
    const numberError = validateRegistrationNumber(number, identifierLabelFor(state.form));
    if (numberError) errors.registration_number = numberError;
  }

  const address = state.address.trim();
  if (address && address.length < LIMITS.addressMin) errors.address = addressShort;

  return errors;
}

/**
 * `PUT` — полная замена: все четыре ключа уходят всегда, пустое — `null`.
 * Пропущенный ключ сервер тоже очистит, но явный `null` читается однозначно.
 */
function toRequest(state: FormState): UpdateLegalRequest {
  const orNull = (value: string) => value.trim() || null;

  return {
    form: state.form || null,
    name: orNull(state.name),
    registration_number: orNull(state.registrationNumber),
    address: orNull(state.address),
  };
}

/** `422` приходит с плоскими ключами (`name`, а не `legal.name`) — раскладываем по полям. */
function fieldErrorsFrom(err: unknown): Errors {
  const errors: Errors = {};
  Object.entries(apiFieldErrors(err)).forEach(([key, message]) => {
    if ((FIELDS as string[]).includes(key)) errors[key as LegalField] = message;
  });
  return errors;
}

/**
 * Реквизиты в настройках профиля: все поля необязательные.
 *
 * Анкета регистрации их не спрашивает — партнёр заполняет их, когда дело
 * дошло до договора, любым подмножеством, и может править. Сохраняются
 * отдельно от профиля (`PUT /partner/legal`) и своей кнопкой: этот запрос
 * проходит и при истёкшей подписке, когда профиль сохранить нельзя.
 *
 * Свежий профиль из пропсов подхватывается, только пока форму не трогали:
 * кабинет перечитывает профиль на каждом переходе, и затирать ввод нельзя.
 */
export const LegalDetailsEditor: React.FC<LegalDetailsEditorProps> = ({ legal }) => {
  const { t } = useLanguage();

  const [state, setState] = useState<FormState>(() => stateFromLegal(legal));
  const [offer, setOffer] = useState<LegalDetail['offer']>(legal?.offer ?? null);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useToast();
  const isTouched = useRef(false);

  useEffect(() => {
    if (isTouched.current) return;
    setState(stateFromLegal(legal));
    setOffer(legal?.offer ?? null);
  }, [legal]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    isTouched.current = true;
    setState((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const found = validate(state, t.cabinet.legalNameShort, t.cabinet.legalAddressShort);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSaving(true);
    try {
      const saved = await partnerApiService.updateLegal(toRequest(state));
      // Сервер нормализует ввод — показываем то, что он сохранил.
      isTouched.current = false;
      setState(stateFromLegal(saved));
      setOffer(saved?.offer ?? null);
      setToast({ message: t.cabinet.legalSaved, tone: 'success' });
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      setFormError(apiErrorMessage(err, t.cabinet.legalSaveError));
    } finally {
      setIsSaving(false);
    }
  };

  const identifierLabel = identifierLabelFor(state.form);

  return (
    <>
      <FormCard title={t.cabinet.legalTitle} hint={t.cabinet.legalHint}>
        <form id={LEGAL_FORM_ID} onSubmit={handleSubmit} noValidate>
          <FieldGrid>
            <Field label={t.cabinet.legalForm} htmlFor="legal-form" error={errors.form}>
              <select
                id="legal-form"
                value={state.form}
                onChange={(e) => update('form', e.target.value as LegalFormValue | '')}
                className="qp-control"
                disabled={isSaving}
                aria-invalid={errors.form ? true : undefined}
              >
                <option value="">{t.cabinet.legalFormNone}</option>
                {LEGAL_FORMS.map((form) => (
                  <option key={form.value} value={form.value}>
                    {form.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t.cabinet.legalName} htmlFor="legal-name" error={errors.name}>
              <input
                id="legal-name"
                type="text"
                value={state.name}
                onChange={(e) => update('name', e.target.value)}
                className="qp-control"
                placeholder={t.cabinet.legalNamePlaceholder}
                maxLength={LIMITS.nameMax}
                autoComplete="organization"
                disabled={isSaving}
                aria-invalid={errors.name ? true : undefined}
              />
            </Field>

            <Field
              label={identifierLabel}
              htmlFor="legal-registration-number"
              hint={t.cabinet.legalNumberHint}
              error={errors.registration_number}
            >
              <input
                id="legal-registration-number"
                type="text"
                inputMode="numeric"
                value={state.registrationNumber}
                onChange={(e) => update('registrationNumber', e.target.value.replace(/\D/g, '').slice(0, 12))}
                className="qp-control"
                placeholder={t.cabinet.legalNumberPlaceholder}
                maxLength={12}
                disabled={isSaving}
                aria-invalid={errors.registration_number ? true : undefined}
              />
            </Field>

            <Field
              label={t.cabinet.legalAddress}
              wide
              htmlFor="legal-address"
              hint={t.cabinet.legalAddressHint}
              error={errors.address}
            >
              <input
                id="legal-address"
                type="text"
                value={state.address}
                onChange={(e) => update('address', e.target.value)}
                className="qp-control"
                placeholder={t.cabinet.legalAddressPlaceholder}
                maxLength={LIMITS.addressMax}
                disabled={isSaving}
                aria-invalid={errors.address ? true : undefined}
              />
            </Field>
          </FieldGrid>
        </form>

        {offer && (
          <p className="qp-faint" style={{ margin: '14px 0 0', fontSize: 12 }}>
            {t.cabinet.legalOfferAccepted} {formatOfferAcceptedAt(offer.accepted_at)} · {t.cabinet.legalOfferEdition}{' '}
            {offer.version}
          </p>
        )}
      </FormCard>

      {formError && (
        <div className="qp-notice qp-notice--error" role="alert">
          {formError}
        </div>
      )}

      <FormFoot note={t.cabinet.legalPrivacyNote}>
        <button type="submit" form={LEGAL_FORM_ID} className="qp-btn qp-btn--primary" disabled={isSaving}>
          {isSaving ? t.cabinet.legalSaving : t.cabinet.legalSave}
        </button>
      </FormFoot>

      <Toast toast={toast} />
    </>
  );
};
